import "./kaltura-embed.scss";

import "./kaltura-types";

import cn from "clsx";
import { ReactNode, useEffect, useRef, useState, useId } from "react";
import { loadScript } from "design-system/hooks/use-script";
//import { createThreePlayScript } from "./three-play-script";
import {
  KALTURA_PARTNER_ID,
  KALTURAV7_SCRIPT_SRC,
  KALTURAV7_UI_CONF_ID,
} from "design-system/utils/constants";
import { setKalturaV7VideoEvents } from "./kalturav7-video-events";
import { useDataLayer } from "frontend/hooks/use-data-layer";

// types/kaltura-player.d.ts
export interface KalturaPlayerEventMap {
  READY: "ready";
  PLAY: "play";
  PLAYING: "playing";
  PAUSE: "pause";
  ENDED: "ended";
  ERROR: "error";
  SEEKED: "seeked";
  SEEKING: "seeking";
  TIME_UPDATE: "timeupdate";
  VOLUMECHANGE: "volumechange";
  FULLSCREENCHANGE: "fullscreenchange";
}

export interface KalturaPlayerInstance {
  loadMedia: (args: { entryId: string }) => void;
  play: () => void;
  destroy: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  addEventListener: (event: string, handler: (event?: any) => void) => void;
  title: string;
  currentTime: number;
  duration: number;
  videoTargetId: string;
  Event: KalturaPlayerEventMap;
}

export interface KalturaEmbedProps {
  entryId: string;
  title?: string;
  withModal?: boolean;
  className?: string;
  duration?: string;
  caption?: ReactNode;
  description?: ReactNode;
  threePlayId?: string;
  threePlayProjectId?: string;
  noModalMobileOverride?: boolean;
  hide?: {
    caption?: boolean;
    focusables?: boolean;
  };
}

/**
 * Component for embedding Kaltura video player widgets. Used within the `MediaAsset` primitive.
 * See the docs for more details: http://player.kaltura.com/docs/api
 *
 * ## See it in use on...
 * - The [article kitchen sink page](/story/example-pages-article-pages-kitchen-sink--story)
 *
 * - **`id: P-016-000-00`**
 */
export function KalturaEmbed({
  hide,
  caption,
  description,
  duration,
  entryId,
  title,
  withModal,
  className,
  threePlayId,
  threePlayProjectId,
}: KalturaEmbedProps) {
  // We need unique ids because the carousel creates duplicates to animate between slides.
  // Replace colons with dashes to avoid invalid HTML IDs.
  const uniqueId = useId().replace(/:/g, "");
  const buttonTextRef = useRef<HTMLDivElement>(null);
  const [play, setPlay] = useState(withModal ?? false);
  const [videoReservePercent, setVideoReservePercent] =
    useState<number>(66.666);
  const [thumbnailObjectFit, setThumbnailObjectFit] = useState<
    "cover" | "contain"
  >("cover");
  const { pushDataLayer } = useDataLayer();
  const pushDataLayerRef = useRef(pushDataLayer);
  const withModalRef = useRef<boolean>(play);

  const videoTargetId = `kaltura-player-${entryId}-${uniqueId}`;
  const threePlayTargetId = `threePlay-${entryId}`;
  const videoButtonId = `${videoTargetId}_playBtn`;

  const hasThreePlay = Boolean(threePlayId && threePlayProjectId);
  const ariaLabel = title ? `Play ${title} video` : `Play video content`;

  // Add ref to track if player is active
  const playerActiveRef = useRef(false);

  const playerInstanceRef = useRef<KalturaPlayerInstance | null>(null);
  const thumbnailRef = useRef<HTMLImageElement>(null);

  const updateThumbnailDimensions = () => {
    const thumbnail = thumbnailRef.current;

    if (!thumbnail || thumbnail.naturalWidth === 0) {
      return;
    }

    setVideoReservePercent(
      (thumbnail.naturalHeight / thumbnail.naturalWidth) * 100,
    );

    setThumbnailObjectFit(
      thumbnail.naturalHeight > thumbnail.naturalWidth ? "contain" : "cover",
    );
  };

  useEffect(() => {
    updateThumbnailDimensions();
  }, [entryId]);

  useEffect(() => {
    pushDataLayerRef.current = pushDataLayer;
  }, [pushDataLayer]);

  useEffect(() => {
    let kalturaPlayer: KalturaPlayerInstance | null = null;

    if (play) {
      loadScript(KALTURAV7_SCRIPT_SRC, {
        onload: () => {
          buttonTextRef.current?.classList.add("hbs-global-visually-hidden");

          if (window.KalturaPlayer) {
            try {
              //remove image + hide play button
              const playBtn = document.getElementById(videoButtonId);
              if (playBtn) {
                playBtn.setAttribute("class", "hbs-kaltura-play-button-hidden");
                playBtn.setAttribute("aria-disabled", "true");
              }

              const playerContainer = document.getElementById(videoTargetId);
              if (playerContainer) {
                playerContainer.innerHTML = "";
              }

              kalturaPlayer = window.KalturaPlayer.setup({
                targetId: videoTargetId,
                provider: {
                  partnerId: `${KALTURA_PARTNER_ID}`,
                  uiconf_id: `${KALTURAV7_UI_CONF_ID}`,
                },
              }) as KalturaPlayerInstance;
              //assign player instance to ref
              playerInstanceRef.current = kalturaPlayer;

              kalturaPlayer.loadMedia({ entryId });
              kalturaPlayer.title = title ? `${title} : ${entryId}` : entryId;
              kalturaPlayer.videoTargetId = videoTargetId;

              if (typeof kalturaPlayer.play === "function") {
                kalturaPlayer.play();
              }

              playerActiveRef.current = true;

              // Commenting out ThreePlay integration for now until we have a v7 replacement
              // if (threePlayId && threePlayProjectId) {
              //   const threePlayScriptSrc = createThreePlayScript({
              //     threePlayId,
              //     threePlayProjectId,
              //     videoTargetId,
              //     threePlayTargetId,
              //   });
              //   loadScript(threePlayScriptSrc, { allowDuplicates: true });
              // }

              setKalturaV7VideoEvents(kalturaPlayer, (event) =>
                pushDataLayerRef.current(event),
              );
            } catch (error) {
              console.error(`Failed to embed Kaltura player:`, error);
              setPlay(false);
              playerActiveRef.current = false;
              playerInstanceRef.current = null;
            }
          }
        },
      });

      return () => {
        //cleanup player instance ref
        if (
          playerInstanceRef.current &&
          typeof playerInstanceRef.current.destroy === "function"
        ) {
          playerInstanceRef.current.destroy();
          playerInstanceRef.current = null;
        }
      };
    }
  }, [
    play,
    videoTargetId,
    entryId,
    threePlayId,
    threePlayProjectId,
    threePlayTargetId,
  ]);

  // Add effect to reset player when destroyed
  useEffect(() => {
    const resetPlayer = () => {
      const playerContainer = document.getElementById(videoTargetId);
      if (playerContainer) {
        const playBtn = document.getElementById(`${videoTargetId}_playBtn`);
        if (playBtn) {
          playBtn.style.display = "block";
        }

        const thumbnail = playerContainer.querySelector(".kWidgetCentered");
        if (thumbnail) {
          (thumbnail as HTMLElement).style.display = "block";
        }

        if (buttonTextRef.current) {
          buttonTextRef.current.classList.remove("hbs-global-visually-hidden");
        }
      }
    };

    // Reset player when play state changes to false
    if (!play) {
      resetPlayer();
    }
  }, [play, videoTargetId]);

  // Add effect to listen for reset events
  useEffect(() => {
    const containerElement = document
      .getElementById(videoTargetId)
      ?.closest(".hbs-kaltura-embed");

    const handleReset = () => {
      setPlay(false);
      playerActiveRef.current = false;
    };

    if (containerElement) {
      containerElement.addEventListener("resetKalturaPlayer", handleReset);
    }

    return () => {
      if (containerElement) {
        containerElement.removeEventListener("resetKalturaPlayer", handleReset);
      }
    };
  }, [videoTargetId]);

  useEffect(() => {
    if (!play) return;

    let rafId = 0;
    let detachListeners: (() => void) | undefined;

    const attachToVideoElement = () => {
      const videoEl = document
        .getElementById(videoTargetId)
        ?.querySelector("video") as HTMLVideoElement | null;

      if (!videoEl) {
        rafId = window.requestAnimationFrame(attachToVideoElement);
        return;
      }

      const updateReservePercent = () => {
        if (videoEl.videoWidth > 0 && videoEl.videoHeight > 0) {
          setVideoReservePercent(
            (videoEl.videoHeight / videoEl.videoWidth) * 100,
          );
        }
      };

      updateReservePercent();

      videoEl.addEventListener("loadedmetadata", updateReservePercent);
      videoEl.addEventListener("resize", updateReservePercent);

      detachListeners = () => {
        videoEl.removeEventListener("loadedmetadata", updateReservePercent);
        videoEl.removeEventListener("resize", updateReservePercent);
      };
    };

    rafId = window.requestAnimationFrame(attachToVideoElement);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      detachListeners?.();
    };
  }, [play, videoTargetId]);

  return (
    <figure className={cn("hbs-media-asset", className)}>
      <div className="hbs-kaltura-embed" data-chromatic="ignore">
        <div
          className="hbs-kaltura-embed__responsive-spacer"
          style={
            thumbnailObjectFit === "contain"
              ? { marginTop: `min(${videoReservePercent}%, 80svh, 900px)` }
              : undefined
          }
        />

        <button
          onClick={() => play !== true && setPlay(true)}
          aria-label={ariaLabel}
          className="kWidgetCentered kWidgetPlayBtn"
          id={`${videoTargetId}_playBtn`}
        ></button>

        <div
          id={videoTargetId}
          className="hbs-kaltura-embed__player"
          style={{ width: "100%", height: "100%" }}
        >
          <div style={{ position: "relative", width: "100%", height: "100%" }}>
            {/* eslint-disable-next-line react/forbid-elements */}
            <img
              ref={thumbnailRef}
              alt=""
              className="kWidgetCentered"
              onLoad={updateThumbnailDimensions}
              style={{ objectFit: thumbnailObjectFit }}
              src={`https://cdnsecakmi.kaltura.com/p/${KALTURA_PARTNER_ID}/thumbnail/entry_id/${entryId}/width/${650}/type/1`}
            />
          </div>
        </div>

        {!withModalRef.current && (
          <div className="hbs-media-asset__kaltura-text" ref={buttonTextRef}>
            <span
              className="hbs-media-asset__kaltura-text-prompt"
              aria-hidden="true"
            >
              Play
            </span>{" "}
            {duration && (
              <span className="hbs-media-asset__kaltura-duration">
                <span className="hbs-global-visually-hidden">
                  Video duration:
                </span>
                {duration}
              </span>
            )}
          </div>
        )}
      </div>

      {hasThreePlay && (
        <div id={threePlayTargetId} className="hbs-threeplay-embed" />
      )}

      {description && (
        <div className="hbs-global-visually-hidden">{description}</div>
      )}

      {!hide?.caption && caption && (
        <figcaption>
          <span className="hbs-media-asset__caption-text">
            <div>{caption}</div>
          </span>
        </figcaption>
      )}
    </figure>
  );
}
